/**
 * Bookkeeping for the imperative simulations: every timer, frame, listener and observer they start goes
 * through here, so the React effect that owns an engine can tear it all down in one call (unmount,
 * StrictMode's double effect, a locale change that remounts the card).
 */
export interface EngineRuntime {
  readonly isAlive: () => boolean;
  later: (callback: () => void, delay: number) => number;
  clearLater: (id: number) => void;
  every: (callback: () => void, delay: number) => number;
  clearEvery: (id: number) => void;
  nextFrame: (callback: (now: number) => void) => number;
  cancelFrame: (id: number) => void;
  listen: <K extends keyof HTMLElementEventMap>(target: HTMLElement, type: K, handler: (event: HTMLElementEventMap[K]) => void) => void;
  onDispose: (cleanup: () => void) => void;
  dispose: () => void;
}

export function createRuntime(): EngineRuntime {
  let alive = true;
  const timeouts = new Set<number>();
  const intervals = new Set<number>();
  const frames = new Set<number>();
  const cleanups: (() => void)[] = [];
  return {
    isAlive: () => alive,
    later: (callback, delay) => {
      const id = window.setTimeout(() => {
        timeouts.delete(id);
        if (alive) callback();
      }, delay);
      timeouts.add(id);
      return id;
    },
    clearLater: (id) => {
      window.clearTimeout(id);
      timeouts.delete(id);
    },
    every: (callback, delay) => {
      const id = window.setInterval(() => {
        if (alive) callback();
      }, delay);
      intervals.add(id);
      return id;
    },
    clearEvery: (id) => {
      window.clearInterval(id);
      intervals.delete(id);
    },
    nextFrame: (callback) => {
      const id = window.requestAnimationFrame((now) => {
        frames.delete(id);
        if (alive) callback(now);
      });
      frames.add(id);
      return id;
    },
    cancelFrame: (id) => {
      window.cancelAnimationFrame(id);
      frames.delete(id);
    },
    listen: (target, type, handler) => {
      target.addEventListener(type, handler);
      cleanups.push(() => target.removeEventListener(type, handler));
    },
    onDispose: (cleanup) => {
      cleanups.push(cleanup);
    },
    dispose: () => {
      alive = false;
      timeouts.forEach((id) => window.clearTimeout(id));
      intervals.forEach((id) => window.clearInterval(id));
      frames.forEach((id) => window.cancelAnimationFrame(id));
      timeouts.clear();
      intervals.clear();
      frames.clear();
      cleanups.splice(0).forEach((cleanup) => cleanup());
    },
  };
}

/** Restart a one-shot CSS effect: drop the class, flush layout, put it back. */
export function restartClass(element: Element, className: string): void {
  element.classList.remove(className);
  element.getBoundingClientRect();
  element.classList.add(className);
}

export function one<T extends Element>(root: ParentNode, selector: string): T {
  const element = root.querySelector<T>(selector);
  if (!element) throw new Error(`v4a simulation: missing ${selector}`);
  return element;
}

export function round1(value: number): number {
  return Math.round(value * 10) / 10;
}

export type Translate = (key: string, values?: Record<string, unknown>) => string;
